import { useEffect, useMemo, useState } from 'react'
import type {
    BodyGenerateTitleDescriptionApiV1GenerationsOrgIdGenerateTitleProductProductIdPost,
    DescriptionToneType,
} from '@/common/api/client/types.gen'
import type { ProductTextsGenerationState } from '../../model/textsGeneration'
import { useProductFlowForm } from '../../model/productFlow'
import { Card } from '@/common/ui/Card'
import { ChoiceGroup } from '@/common/ui/ChoiceGroup'
import { CollapsibleCard } from '@/common/ui/CollapsibleCard'
import { Button } from '@/common/ui/Form/Button'
import { Fieldset } from '@/common/ui/Form/Fieldset'
import { Textarea } from '@/common/ui/Form/Textarea'
import { StickyBottomBar } from '@/common/ui/StickyBottomBar/StickyBottomBar'
import { TokenAmount } from '@/common/ui/TokenAmount'

const DESCRIPTION_TONES: Array<{ label: string; value: DescriptionToneType }> = [
    { label: 'Сдержанно', value: 'RESTRAINED' },
    { label: 'Тепло', value: 'WARM' },
    { label: 'Технично', value: 'TECHNICALLY' },
    { label: 'Бодро', value: 'CHEERFULLY' },
    { label: 'Продающе', value: 'SELLING' },
]

type TextsFormValues = BodyGenerateTitleDescriptionApiV1GenerationsOrgIdGenerateTitleProductProductIdPost

const defaultTextsValues = (): TextsFormValues => ({
    description_tone: 'RESTRAINED',
    key_words: '',
})

type TextsFormProps = {
    texts: ProductTextsGenerationState
}

export const TextsForm = ({ texts }: TextsFormProps) => {
    const {
        isProductLoading,
        submitGeneration,
        generationMutation,
        detailFormPipelineLoading,
        orgId,
        productId,
        product,
    } = texts
    const [isParamsOpen, setIsParamsOpen] = useState(true)

    const { register, handleSubmit, watch, setValue, formState, clearErrors } = useProductFlowForm<TextsFormValues>({
        defaultValues: defaultTextsValues(),
        generationMutation,
    })

    const selectedTone = watch('description_tone')
    const toneActiveIndex = useMemo(
        () =>
            Math.max(
                0,
                DESCRIPTION_TONES.findIndex(tone => tone.value === selectedTone)
            ),
        [selectedTone]
    )

    const isSubmitting = detailFormPipelineLoading
    const canSubmit = Boolean(orgId && productId && product)
    const isFieldsDisabled = detailFormPipelineLoading

    const hasParamsErrors = Boolean(formState.errors.description_tone || formState.errors.key_words)

    useEffect(() => {
        if (hasParamsErrors) {
            setIsParamsOpen(true)
        }
    }, [hasParamsErrors])

    return (
        <>
            {isProductLoading && (
                <div className='flex justify-center items-center h-32' role='status' aria-live='polite'>
                    Загрузка...
                </div>
            )}
            {!isProductLoading && (
                <form
                    onSubmit={handleSubmit(values => {
                        clearErrors()
                        submitGeneration(values)
                    })}
                >
                    <Card type='result-last' cardBodyClassName='p-[20px]!'>
                        <h3>Настройки генерации текста карточки</h3>

                        <div className='flex flex-col gap-3'>
                            <CollapsibleCard
                                title='Параметры генерации'
                                isOpen={isParamsOpen}
                                onToggle={setIsParamsOpen}
                            >
                                <Fieldset
                                    className='min-w-0'
                                    label='Тональность описания'
                                    errorText={formState.errors.description_tone?.message}
                                >
                                    <div className='min-w-0 w-full max-w-full overflow-hidden'>
                                        <ChoiceGroup
                                            className='w-full max-w-full'
                                            items={DESCRIPTION_TONES.map(tone => tone.label)}
                                            activeIndex={toneActiveIndex}
                                            onChange={index => {
                                                const nextTone = DESCRIPTION_TONES[index]?.value
                                                if (!nextTone) {
                                                    return
                                                }
                                                setValue('description_tone', nextTone, { shouldDirty: true })
                                            }}
                                        />
                                    </div>
                                </Fieldset>
                                <Fieldset label='Ключевые слова' errorText={formState.errors.key_words?.message}>
                                    <Textarea
                                        size='md'
                                        disabled={isFieldsDisabled}
                                        className='w-full min-h-[56px]'
                                        placeholder='Укажите через запятую ключевые слова для вашего текста'
                                        aria-label='Ключевые слова'
                                        {...register('key_words')}
                                    />
                                </Fieldset>
                            </CollapsibleCard>
                        </div>

                        {formState.errors.root?.message && (
                            <p className='text-sm text-red-400 mt-3' role='alert'>
                                {formState.errors.root.message}
                            </p>
                        )}
                    </Card>
                    <StickyBottomBar contentClassName='pr-[100px] xl:pr-[45px]'>
                        <span className='md:block hidden text-sm text-[var(--pixorion-gray-2)]'>1–2 минуты</span>
                        <Button
                            type='submit'
                            variant={isSubmitting ? 'outline' : 'filled'}
                            disabled={isSubmitting || !canSubmit}
                            loading={isSubmitting}
                            size='lg'
                            aria-label='Запустить генерацию текста карточки'
                        >
                            {isSubmitting ? 'Генерация…' : 'Запустить генерацию'}
                            {!isSubmitting && <TokenAmount value={25} />}
                        </Button>
                    </StickyBottomBar>
                </form>
            )}
        </>
    )
}
